/**
 * 商品退货调整
 */
import React, { useImperativeHandle, useState, useRef, useContext } from 'react';
import { Modal, Form, message } from 'antd';
import intl from 'react-intl-universal';

import BigNumber, { Calculator, format2Decimal, parseYuan2Currency } from '@/utils/number';
import InputWithHelper from '@/components/Reservation/NumberInput/InputWithHelper';
import NumberInput from '@/components/Reservation/NumberInput';
import IntlContext from '@/components/Intl/IntlContext';

import { isEqualVal, isGreaterVal } from './helper';
import styles from './index.module.scss';

const SceneConfigMap = {
  GOODS_RETURN: {
    title: intl.get('cmp_order_goods_refund_modal_title_return'),
    message: intl.get('cmp_order_goods_refund_modal_message_return'),
  },
  GOODS_REVERSAL: {
    title: intl.get('cmp_order_goods_refund_modal_title_reversal'),
    message: intl.get('cmp_order_goods_refund_modal_message_reversal'),
  },
};

interface IProps {
  scene?: keyof typeof SceneConfigMap;
  onConfirm: (data: Partial<any>) => void;
}

interface EditForm {
  returnedCount: string; // 退货数量
  leftTotalValue: string; // 剩余小计
}

interface RelateData {
  isSameData: boolean; // 数据是否相同
  leftCount: string; // 剩余数量
}

export interface GoodsInfo {
  indexId: string;
  goodsCode: string; // sku编码
  goodsName: string; // 商品名称
  specification: string; // 规格
  goodsImage: string; // 商品图片
  subject: string; // 科目
  price: string; // 售价（元）
  standardPrice: string; // 标价（元）
  initCount: string; // 初始数量
  currentCount: string; // 当前数量
  initTotalValue: string; // 初始小计金额(元)
  currentTotalValue: string; // 当前小计金额(元)
}

export interface GoodsRefundModalRef {
  showModal: (data: any) => void;
}

const { Item: FormItem, useForm } = Form;
const noop = () => {};

export default React.forwardRef<GoodsRefundModalRef, IProps>((props, ref) => {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [relateData, setRelateData] = useState<RelateData>({ isSameData: true, leftCount: '0' }); // 关联数据
  const editDataRef = useRef<GoodsInfo>({} as GoodsInfo);
  const [formField] = useForm<EditForm>();
  const intlCtx = useContext(IntlContext);

  const initEditData = editDataRef.current;
  const { initCount, initTotalValue, price, standardPrice } = initEditData;
  const sceneConfig = SceneConfigMap[props.scene || 'GOODS_RETURN'];

  // 初始化弹框
  const showModal = (data: GoodsInfo) => {
    formField.setFields([
      { name: 'returnedCount', value: '0', errors: [] },
      { name: 'leftTotalValue', value: data.initTotalValue, errors: [] },
    ]);
    editDataRef.current = {
      ...data,
    };
    setRelateData({ isSameData: true, leftCount: data.initCount });
    setModalVisible(true);
  };

  // ref 引用
  useImperativeHandle(ref, () => ({ showModal }), []);

  // 关闭弹框
  const closeModal = () => {
    setModalVisible(false);
  };

  // 确认提交
  const handleSubmitClick = async () => {
    try {
      const values = await formField.validateFields();
      const { returnedCount, leftTotalValue } = values;
      props.onConfirm({
        indexId: editDataRef.current.indexId,
        currentCount: new BigNumber(initCount).minus(returnedCount).valueOf(),
        currentTotalValue: format2Decimal(leftTotalValue),
      });
      setModalVisible(false);
    } catch (err) {
      message.warning(err?.errorFields[0].errors);
    }
  };

  // 校验退货数量
  const validateReturnVal = (_, val) => {
    return new Promise((resolve, reject) => {
      let errMessage = '';
      switch (true) {
        case val === '':
          errMessage = intl.get('cmp_order_goods_refund_modal_count_error_null');
          break;
        case isGreaterVal(val, initCount):
          errMessage = intl.get('cmp_order_goods_refund_modal_count_error_range', {
            max: initCount,
          });
          break;
        default:
          errMessage = '';
          break;
      }
      if (errMessage) {
        reject(errMessage);
      } else {
        resolve(null);
      }
    });
  };

  // 退货数量修改
  const handleReturnedCountChange = (val) => {
    // 只能输入整数
    if (/^\d*$/.test(val)) {
      // 同步修改剩余小计 商品小计 -（实际售卖单价 x 退货数量）
      let leftVal = new Calculator(initTotalValue)
        .minus(new BigNumber(val).multipliedBy(price))
        .getValue();
      if (isEqualVal(val, initCount)) {
        // 退货数量 等于 可退数量 强制为0
        leftVal = '0';
      }
      formField.setFields([
        // 剩余数量小于0 取值为0
        { name: 'leftTotalValue', value: isGreaterVal(leftVal, '0') ? leftVal : '0', errors: [] },
      ]);
      return val;
    }
    // 不符合规则 返回原始值
    return formField.getFieldValue('returnedCount') || '';
  };

  // 校验剩余小计
  const validateLeftVal = (_, val) => {
    return new Promise((resolve, reject) => {
      let errMessage = '';
      switch (true) {
        case val === '':
          errMessage = intl.get('cmp_order_goods_refund_modal_amount_error_null');
          break;
        case relateData.leftCount === '0' && val !== '0':
          errMessage = intl.get('cmp_order_goods_refund_modal_amount_error_zero');
          break;
        case isGreaterVal(val, initTotalValue):
          errMessage = intl.get('cmp_order_goods_refund_modal_amount_error_range', {
            max: initTotalValue,
          });
          break;
        default:
          errMessage = '';
          break;
      }
      if (errMessage) {
        reject(errMessage);
      } else {
        resolve(null);
      }
    });
  };

  // 剩余小计修改
  const handleLeftValueChange = (val) => {
    if (/^(0|[1-9]\d{0,4})(\.\d{0,2})?$|^$/.test(val)) {
      // 两位小数
      return val;
    }
    // 不符合规则 返回原始值
    return formField.getFieldValue('leftTotalValue') || '';
  };

  const handleFormUpdate = () => {
    const formValue = formField.getFieldsValue();
    const leftCount = new Calculator(initCount).minus(formValue.returnedCount || 0).getValue();
    setRelateData({
      // 退货数量为0 且 剩余小计 等于 初始小计
      isSameData:
        isEqualVal(formValue.returnedCount, '0') &&
        isEqualVal(formValue.leftTotalValue, initEditData.initTotalValue),
      leftCount: isGreaterVal('0', leftCount) ? '0' : leftCount,
    });
  };

  return (
    <Modal
      title={sceneConfig.title}
      centered
      open={modalVisible}
      onCancel={closeModal}
      onOk={handleSubmitClick}
      okButtonProps={{
        disabled:
          relateData.isSameData ||
          (relateData.leftCount === '0' && formField.getFieldValue('leftTotalValue') !== '0'),
      }}
    >
      <Form className={styles.editFormBody} form={formField} onValuesChange={handleFormUpdate}>
        <FormItem label={intl.get('cmp_order_goods_refund_modal_standard_label')}>
          {parseYuan2Currency(standardPrice)}
        </FormItem>
        <FormItem label={intl.get('cmp_order_goods_refund_modal_price_label')}>
          {parseYuan2Currency(price)}
        </FormItem>
        <FormItem label={intl.get('cmp_order_goods_refund_modal_total_label')}>
          {parseYuan2Currency(initTotalValue)}
        </FormItem>
        <FormItem label={intl.get('cmp_order_goods_refund_modal_count_label')}>
          {initCount}
        </FormItem>
        <FormItem
          label={intl.get('cmp_order_goods_refund_modal_return_label')}
          name="returnedCount"
          className={styles.returnValueItem}
          rules={[{ validator: validateReturnVal }]}
          getValueFromEvent={handleReturnedCountChange}
        >
          <InputWithHelper onChange={noop} />
        </FormItem>
        <FormItem label={intl.get('cmp_order_goods_refund_modal_left_label')}>
          {relateData.leftCount}
        </FormItem>
        <FormItem
          label={intl.get('cmp_order_goods_refund_modal_value_label')}
          name="leftTotalValue"
          rules={[{ validator: validateLeftVal }]}
          getValueFromEvent={handleLeftValueChange}
        >
          <NumberInput
            prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
            className={styles.leftValueItem}
            placeholder={intl.get('cmp_order_goods_refund_modal_value_placeholder')}
            onChange={noop}
          />
        </FormItem>
        <p>{sceneConfig.message}</p>
      </Form>
    </Modal>
  );
});
